// Preguntar: el mecanismo `think` de gbrain — respuesta sintetizada con citas
// [[slug]] clicables y análisis de huecos, vía brain-ask (híbrido con embeddings).

import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { Brain, Send } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useBrainAsk } from '@/hooks/useBrain'
import { BRAIN_TYPE_COLORS, BRAIN_TYPE_LABELS, type BrainAskResponse } from '@/types/brain'
import { BrainMarkdown } from './BrainMarkdown'

interface Props {
  onOpenPage: (slug: string) => void
}

const SUGGESTIONS = [
  '¿En qué va el deal de BanCoppel y cuál es el próximo paso?',
  '¿Qué certificaciones necesitamos para tarjeta presente?',
  '¿Qué se dijo en la última llamada con Quálitas?',
  '¿Cuál es la economía del proyecto ($ARR, rampa)?',
]

export function PreguntarTab({ onOpenPage }: Props) {
  const [question, setQuestion] = useState('')
  const [result, setResult] = useState<BrainAskResponse | null>(null)
  const ask = useBrainAsk()

  const submit = async (q?: string) => {
    // In-flight guard: chips/Cmd+Enter must not race a pending ask (a stale
    // answer could win over the newer question).
    if (ask.isPending) return
    const text = (q ?? question).trim()
    if (text.length < 3) return
    if (q) setQuestion(q)
    // Limpia la respuesta previa: si la nueva pregunta falla, no debe quedar
    // la respuesta VIEJA renderizada bajo la pregunta nueva.
    setResult(null)
    try {
      setResult(await ask.mutateAsync(text))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'La pregunta falló — intenta de nuevo')
    }
  }

  const ago = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es })

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="space-y-2">
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit() }
          }}
          rows={3}
          placeholder="Pregúntale al cerebro del proyecto… responde solo con lo que sabe y te dice qué le falta"
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => submit(s)}
                className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted"
              >
                {s}
              </button>
            ))}
          </div>
          <Button onClick={() => submit()} disabled={ask.isPending || question.trim().length < 3}>
            <Send className="mr-1.5 h-3.5 w-3.5" />
            {ask.isPending ? 'Pensando…' : 'Preguntar'}
          </Button>
        </div>
      </div>

      {ask.isPending && (
        <Card className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
          <Brain className="h-5 w-5 animate-pulse text-teal-500" />
          Buscando en el cerebro (vector + texto) y sintetizando con citas…
        </Card>
      )}

      {result && !ask.isPending && (
        <Card className="space-y-4 p-5">
          <BrainMarkdown content={result.answer_md} onOpenPage={onOpenPage} />
          {result.sources.length > 0 && (
            <div className="border-t border-border/40 pt-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Fuentes ({result.sources.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {result.sources.map((s) => (
                  <button
                    key={s.slug}
                    type="button"
                    onClick={() => onOpenPage(s.slug)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs hover:bg-muted"
                    title={`actualizada ${ago(s.updated_at)}`}
                  >
                    <Badge variant="secondary" className={`${BRAIN_TYPE_COLORS[s.page_type]} px-1.5 py-0 text-[10px]`}>
                      {BRAIN_TYPE_LABELS[s.page_type]}
                    </Badge>
                    {s.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
